// SAD-1000: the drill-down list's words and URL. The list keeps the
// Command Center's filter in the URL (filters.ts) plus its own two
// parameters: the group-by, and the date open in the side panel.
import { DUE_OPTIONS, FILED_STATES, OWED_STATES, writeFilter } from './filters';
export const LIST_PATH = '/delivery/command-center/deliverables';
export const GROUP_OPTIONS = [
    {
        value: 'program',
        label: 'Program'
    },
    {
        value: 'team',
        label: 'Team'
    },
    {
        value: 'owner',
        label: 'Owner'
    },
    {
        value: 'status',
        label: 'Status'
    },
    {
        value: 'due_week',
        label: 'Due week'
    },
    {
        value: 'none',
        label: 'No grouping'
    }
];
/** The default grouping is by program; "none" is spelled out in the URL. */ export function parseGroup(params) {
    const g = params.get('group') ?? '';
    return GROUP_OPTIONS.some((o)=>o.value === g) ? g : 'program';
}
/** The server's group_by for a grouping. */ export const serverGroup = (g)=>g === 'none' ? '' : g;
const ID = /^[A-Za-z0-9_-]{1,64}$/;
/** The date open in the side panel: ?occ=<occurrence>&in=<program>. */ export function parsePanel(params) {
    const occ = params.get('occ') ?? '';
    const program = params.get('in') ?? '';
    return ID.test(occ) && ID.test(program) ? {
        occ,
        program
    } : null;
}
/** The list for a filter, with a grouping (the default left out). */ export function listHrefFor(filter, group = 'program') {
    const qs = writeFilter(filter);
    if (group !== 'program') qs.set('group', group);
    const s = qs.toString();
    return `${LIST_PATH}${s ? `?${s}` : ''}`;
}
const sameSet = (a, b)=>a.length === b.length && [
        ...a
    ].sort().join(',') === [
        ...b
    ].sort().join(',');
const TITLE = {
    late: 'Late deliverables',
    at_risk: 'At-risk deliverables',
    due_soon: 'Deliverables due in 14 days',
    not_yet_due: 'Deliverables not yet due',
    awaiting_review: 'Deliverables waiting on approval',
    rejected: 'Returned deliverables',
    upcoming: 'Upcoming deliverables',
    approved: 'Approved deliverables',
    on_hold: 'Paused deliverables (program on hold)'
};
const RULE = {
    late: 'Past their due date with nothing filed.',
    at_risk: 'Due within 5 working days with no draft uploaded.',
    due_soon: 'Due in the next 14 days with nothing filed yet.',
    not_yet_due: 'Due later, or due soon but not yet at risk.',
    awaiting_review: 'Filed and waiting for a reviewer’s decision.',
    rejected: 'Returned by a reviewer, waiting for a new filing.',
    upcoming: 'Due more than 14 days from now.',
    approved: 'Approved by the final reviewer.',
    on_hold: 'Due on or after the day the program went on hold, so not counted as late.'
};
/** The page title for the status filter. */ export function listTitle(states) {
    if (states.length === 1 && TITLE[states[0]]) return TITLE[states[0]];
    if (sameSet(states, FILED_STATES)) return 'Filed deliverables';
    if (sameSet(states, OWED_STATES)) return 'Deliverables late or still owed';
    return 'Deliverables';
}
const day = (iso)=>new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
    });
/** One sentence: what the status means, then over which dates. */ export function listDescription(filter) {
    const rule = filter.states.length === 1 ? RULE[filter.states[0]] ?? '' : sameSet(filter.states, FILED_STATES) ? 'Approved, waiting on approval or returned.' : sameSet(filter.states, OWED_STATES) ? 'Late, or not yet approved.' : '';
    const when = filter.from && filter.to ? filter.from === filter.to ? `due ${day(filter.from)}` : `due ${day(filter.from)} to ${day(filter.to)}` : filter.to ? `due by ${day(filter.to)}` : filter.from ? `due from ${day(filter.from)}` : DUE_OPTIONS.find((o)=>o.value === filter.due)?.label.toLowerCase() ?? '';
    const period = filter.period === 'all' ? 'Every date' : 'Each program’s current period';
    return [
        rule,
        `${period}${when ? `, ${when}` : ''}.`
    ].filter(Boolean).join(' ');
}
const plural = (n, one, many = `${one}s`)=>`${n.toLocaleString()} ${n === 1 ? one : many}`;
/**
 * The summary band: how many, how late the oldest is, where most of them
 * are, how many are at risk. Facts only, each a number.
 */ export function bandParts(body, groupBy) {
    if (!body) return [];
    const sc = body.stateCounts ?? {};
    const parts = [
        plural(body.total, 'deliverable')
    ];
    const late = sc.late ?? 0;
    if (late > 0) parts[0] = late === body.total ? `${plural(late, 'deliverable')} late` : parts[0];
    if (late > 0 && late < body.total) parts.push(`${late.toLocaleString()} late`);
    if (body.oldestDaysLate > 0) parts.push(`oldest ${plural(body.oldestDaysLate, 'day')} late`);
    if (body.atRisk > 0) parts.push(`${body.atRisk.toLocaleString()} at risk`);
    const waiting = sc.awaiting_review ?? 0;
    if (waiting > 0) parts.push(`${waiting.toLocaleString()} waiting on approval`);
    if (groupBy !== 'none' && groupBy !== 'status' && groupBy !== 'due_week' && body.groups.length > 1) {
        const top = [
            ...body.groups
        ].sort((a, b)=>b.count - a.count)[0];
        if (top && top.count < body.total) {
            const where = top.sublabel ? `${top.label} (${top.sublabel})` : top.label;
            parts.push(`${top.count.toLocaleString()} on ${where}`);
        }
    }
    return parts;
}
/** "Oct 8" and how far it is: "12 days ago", "today", "in 3 days". */ export function dueWords(iso, today) {
    if (!iso) return {
        date: 'No due date',
        rel: ''
    };
    const d = new Date(`${iso}T00:00:00Z`);
    const t = new Date(`${today}T00:00:00Z`);
    const days = Math.round((d.getTime() - t.getTime()) / 86400000);
    // The year only when it is not this one.
    const date = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: d.getUTCFullYear() === t.getUTCFullYear() ? undefined : 'numeric',
        timeZone: 'UTC'
    });
    const rel = days === 0 ? 'today' : days === 1 ? 'tomorrow' : days === -1 ? 'yesterday' : days < 0 ? `${(-days).toLocaleString()} days ago` : `in ${days.toLocaleString()} days`;
    return {
        date,
        rel
    };
}
